import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import apiClient from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../components/ui/Toast';
import { formatDate, formatDateTime, formatEUR } from '../../lib/format';
import { cn } from '../../utils/cn';
import {
  cmdHeaders,
  returnError,
  CONDITIONS,
  condLabel,
  Field,
  LegalNote,
  msgText,
  Panel,
  reasonLabel,
  returnsBase,
  StatusBadge,
  tdCls,
  thCls,
  timelineText,
  Track,
  useReturnsRole,
  type ReturnDetail as Detail,
  type ReturnLine,
} from '../../components/returns/returnsShared';

/**
 * One return (returns v2): where it stands (3-step tracker), its parts with the money, and the
 * next step for whoever looks at it — a manager decides, the counter or the warehouse receives and
 * controls, the counter gives a private customer's money back, a manager grants a warranty without
 * waiting for the supplier. The garage portal sees the same page without the internal parts.
 */

const DEFECT = ['DEFECT_NEW', 'TRANSPORT', 'WARRANTY'];

interface Ctx {
  r: Detail;
  busy: boolean;
  run: (key: string, fn: () => Promise<unknown>, ok?: string) => Promise<void>;
  err: { key: string; text: string; code?: string } | null;
  setErr: (e: { key: string; text: string; code?: string } | null) => void;
  can: ReturnType<typeof useReturnsRole>['can'];
  userId: string | undefined;
  portal: boolean;
  viewer: boolean;
  settledAll: boolean;
  unsettledReady: ReturnLine[];
  ErrLine: (p: { k: string }) => JSX.Element | null;
}
const errCode = (e: unknown) =>
  (e as { response?: { data?: { code?: string } } })?.response?.data?.code;

export default function ReturnDetail() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const { portal, viewer, can, userId } = useReturnsRole();
  const base = returnsBase(portal);
  const [r, setR] = useState<Detail | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ key: string; text: string; code?: string } | null>(null);

  const load = useCallback(() => {
    apiClient
      .get<Detail>(`/returns/${id}/full`)
      .then((x) => setR(x.data))
      .catch((e) => {
        if (errCode(e) === 'RETURN_NOT_FOUND') setMissing(true);
        else showApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(load, [load]);

  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      if (ok) push({ tone: 'success', message: ok });
      load();
    } catch (e) {
      if ((e as { response?: unknown })?.response)
        setErr({ key, text: returnError(t, e), code: errCode(e) });
      else showApiError(e);
    } finally {
      setBusy(false);
    }
  };
  const ErrLine = ({ k }: { k: string }) =>
    err?.key === k ? (
      <p className="text-sm font-medium text-destructive" role="alert">
        {err.text}
      </p>
    ) : null;

  if (missing)
    return (
      <div className="px-4 py-12 text-center text-sm text-muted-foreground">
        {t('returnsV2.detail.notFound')}
      </div>
    );
  if (!r)
    return (
      <div className="px-4 py-12 text-center text-sm text-muted-foreground">
        {t('returnsV2.loading')}
      </div>
    );

  const c: Ctx = {
    r,
    busy,
    run,
    err,
    setErr,
    can,
    userId,
    portal,
    viewer,
    settledAll: false,
    unsettledReady: [],
    ErrLine,
  };
  const b2b = r.channel === 'B2B';
  const legal = [...new Set(r.lines.map((l) => l.legalNote).filter(Boolean))] as string[];
  const unsettledReady = r.lines.filter(
    (l) => l.decision === 'ACCEPTED' && l.control && !l.settled
  );
  const settledAll = r.status === 'INSPECTED' && unsettledReady.length === 0;
  c.settledAll = settledAll;
  c.unsettledReady = unsettledReady;
  const vatOf = (l: ReturnLine) => 1 + (l.vat ?? 20) / 100;
  const ttcOf = (k: 'itemHt' | 'feeHt' | 'depositHt' | 'depreciationHt') =>
    r.lines.reduce((sum, l) => sum + l.amounts[k] * vatOf(l), 0);

  return (
    <div className="app-tokens">
      <p className="mb-2">
        <Link
          to={base}
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />{' '}
          {portal ? t('returnsV2.detail.backPortal') : t('returnsV2.detail.back')}
        </Link>
      </p>
      <div className="mb-4">
        <h1 className="flex flex-wrap items-center gap-3 text-[22px] font-bold leading-tight">
          <span className="font-mono">{r.returnNumber}</span>
          <StatusBadge status={r.status} refundInProgress={settledAll} />
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {!portal && (
            <>
              <b className="text-foreground">{r.customerName}</b> ·{' '}
              {t(`returnsV2.customerType.${r.customerType ?? 'B2C'}`)} ·{' '}
            </>
          )}
          {t(
            r.origin === 'PORTAL' ? 'returnsV2.detail.fromPortal' : 'returnsV2.detail.fromCounter',
            { date: formatDate(r.requestedOn ?? r.createdAt), by: r.createdBy ?? '' }
          )}
          {r.rmaUntil && r.status === 'APPROVED' && (
            <>
              {' '}
              ·{' '}
              {t(
                r.pickup === 'DRIVER'
                  ? 'returnsV2.detail.expectedDriver'
                  : 'returnsV2.detail.expectedCounter',
                { until: formatDate(r.rmaUntil) }
              )}
            </>
          )}
          {r.comment && <> · « {r.comment} »</>}
        </p>
      </div>

      {r.legacy && (
        <div className="mb-3 rounded-md bg-primary-soft px-3 py-2 text-sm text-primary">
          {t('returnsV2.detail.legacy')}{' '}
          {!portal && (
            <Link className="underline" to="/returns/legacy">
              {t('returnsV2.detail.legacyLink')}
            </Link>
          )}
        </div>
      )}

      {r.retractRefundDue && !portal && (
        <div
          className={cn(
            'mb-3 rounded-md px-3 py-2 text-sm font-medium',
            r.retractRefundedOn ? 'bg-success/[.12] text-success' : 'bg-primary-soft text-primary'
          )}
        >
          {t('returnsV2.detail.retract', { due: formatDate(r.retractRefundDue) })}
          {r.retractRefundedOn
            ? ` ${t('returnsV2.detail.retractDone', { date: formatDate(r.retractRefundedOn) })}`
            : ` ${t('returnsV2.detail.retractWait')}`}
          {r.retractShippingProof &&
            ` ${t('returnsV2.detail.retractProof', { date: formatDate(r.retractShippingProof) })}`}
        </div>
      )}

      {!r.legacy && (
        <Panel className="mb-3">
          <Track r={r} />
        </Panel>
      )}

      {portal && r.status === 'APPROVED' && (
        <div className="mb-3 flex flex-wrap items-center gap-5 rounded-xl border-2 border-dashed border-primary bg-card px-5 py-4">
          <div>
            <span className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('returnsV2.detail.rmaLabel')}
            </span>
            <b className="font-mono text-2xl">{r.returnNumber}</b>
          </div>
          <p className="min-w-[16rem] flex-1 text-sm">
            {t(
              r.pickup === 'DRIVER' ? 'returnsV2.detail.rmaDriver' : 'returnsV2.detail.rmaCounter',
              { until: formatDate(r.rmaUntil) }
            )}
          </p>
        </div>
      )}

      <Panel title={t('returnsV2.detail.parts')} className="mb-3">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className={thCls}>{t('returnsV2.detail.col.part')}</th>
                <th className={thCls}>{t('returnsV2.detail.col.purchase')}</th>
                <th className={cn(thCls, 'text-right')}>{t('returnsV2.detail.col.qty')}</th>
                <th className={thCls}>{t('returnsV2.detail.col.reason')}</th>
                <th className={thCls}>{t('returnsV2.detail.col.decision')}</th>
                <th className={thCls}>{t('returnsV2.detail.col.control')}</th>
                <th className={cn(thCls, 'text-right')}>{t('returnsV2.detail.col.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {r.lines.map((l) => (
                <LineRow key={l.id} l={l} portal={portal} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid gap-3 border-t px-4 py-3 md:grid-cols-[minmax(16rem,24rem)_1fr]">
          <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm">
            <dt className="text-muted-foreground">{t('returnsV2.detail.tot.parts')}</dt>
            <dd className="text-right tabular-nums">{formatEUR(ttcOf('itemHt'))}</dd>
            {r.totals.feeHt > 0 && (
              <>
                <dt className="text-muted-foreground">{t('returnsV2.detail.tot.fee')}</dt>
                <dd className="text-right tabular-nums">−{formatEUR(ttcOf('feeHt'))}</dd>
              </>
            )}
            {r.totals.depositHt > 0 && (
              <>
                <dt className="text-muted-foreground">{t('returnsV2.detail.tot.core')}</dt>
                <dd className="text-right tabular-nums">{formatEUR(ttcOf('depositHt'))}</dd>
              </>
            )}
            {r.totals.depreciationHt > 0 && (
              <>
                <dt className="text-muted-foreground">{t('returnsV2.detail.tot.depreciation')}</dt>
                <dd className="text-right tabular-nums">−{formatEUR(ttcOf('depreciationHt'))}</dd>
              </>
            )}
            {r.totals.shippingTtc > 0 && (
              <>
                <dt className="text-muted-foreground">{t('returnsV2.detail.tot.shipping')}</dt>
                <dd className="text-right tabular-nums">{formatEUR(r.totals.shippingTtc)}</dd>
              </>
            )}
            <dt className="text-muted-foreground">{t('returnsV2.detail.tot.vat')}</dt>
            <dd className="text-right tabular-nums">{formatEUR(r.totals.vat)}</dd>
            <dt className="border-t pt-1 font-semibold">
              {['CLOSED', 'REFUNDED'].includes(r.status)
                ? t(b2b ? 'returnsV2.detail.tot.credited' : 'returnsV2.detail.tot.given')
                : t('returnsV2.detail.tot.planned')}
            </dt>
            <dd className="border-t pt-1 text-right font-semibold tabular-nums">
              {formatEUR(r.totals.total)}
            </dd>
          </dl>
          <div>
            {legal.map((c) => (
              <LegalNote key={c} code={c} />
            ))}
          </div>
        </div>
      </Panel>

      <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="grid min-w-0 gap-3">
          {!r.legacy && (portal ? <PortalActions c={c} /> : <StaffActions c={c} />)}
        </div>
        <div className="grid min-w-0 gap-3">
          <Panel
            title={portal ? t('returnsV2.detail.timeline') : t('returnsV2.detail.timelineStaff')}
          >
            <ol className="grid gap-2 px-4 py-3 text-sm">
              {r.timeline.map((s, i) => (
                <li key={i} className="grid grid-cols-[6rem_1fr] gap-2">
                  <time className="text-xs tabular-nums text-muted-foreground">
                    {formatDate(s.date)}
                  </time>
                  <span>{timelineText(t, s)}</span>
                </li>
              ))}
              {!r.timeline.length && <li className="text-muted-foreground">—</li>}
            </ol>
          </Panel>
          <Documents c={c} />
          {!portal && r.refunds.length > 0 && (
            <Panel
              title={t('returnsV2.detail.refunds')}
              actions={
                <Link to="/refunds" className="text-xs text-primary hover:underline">
                  {t('returnsV2.detail.refundsLink')}
                </Link>
              }
            >
              <ul className="grid gap-1 px-4 py-3 text-sm">
                {r.refunds.map((f) => (
                  <li key={f.id} className="flex justify-between gap-3">
                    <span>
                      {t(`refunds.channel.${f.channel}`, { defaultValue: f.channel })} ·{' '}
                      {t(`refunds.status.${f.status}`, { defaultValue: f.status })}
                    </span>
                    <b className="tabular-nums">{formatEUR(Number(f.amount))}</b>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
          {!portal && (
            <Panel title={t('returnsV2.detail.internal')}>
              <ol className="grid gap-2 px-4 py-3 text-sm">
                {r.internal.map((x, i) => (
                  <li key={i} className="grid grid-cols-[6rem_1fr] gap-2">
                    <time className="text-xs tabular-nums text-muted-foreground">
                      {formatDateTime(x.at)}
                    </time>
                    <span>
                      {t(`returnsV2.audit.${x.action}`, { defaultValue: x.action })}
                      <span className="block text-xs text-muted-foreground">
                        {x.by ?? t('returnsV2.detail.system')}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}

function Documents({ c }: { c: Ctx }) {
  const { r, portal } = c;
  const { t } = useTranslation();
  const d = r.documents;
  const items: Array<{ id: string; label: string; ttc: number; to?: string }> = [
    ...d.creditNotes.map((c) => ({
      id: c.number,
      label: t('returnsV2.doc.AVO'),
      ttc: c.ttc,
      to: portal ? undefined : `/credit-notes?open=${c.id}`,
    })),
    ...(d.nextInvoice
      ? [
          {
            id: d.nextInvoice.invoiceNumber ?? t('returnsV2.doc.nextInvoice'),
            label: t('returnsV2.doc.RECAP'),
            ttc: -d.nextInvoice.ttc,
          },
        ]
      : []),
    ...d.vouchers.map((v) => ({
      id: v.number,
      label: t('returnsV2.doc.VOUCHER', { until: formatDate(v.validUntil) }),
      ttc: v.ttc,
    })),
    ...d.exchanges.map((x) => ({ id: x.number, label: t('returnsV2.doc.EXCHANGE'), ttc: 0 })),
    ...d.supplierReturns.map((s) => ({
      id: s.number,
      label: t('returnsV2.doc.BRF', { supplier: s.supplierName }),
      ttc: NaN,
      to: '/returns/suppliers',
    })),
  ];
  return (
    <Panel title={t('returnsV2.detail.documents')}>
      {items.length ? (
        <ul className="grid gap-2 px-4 py-3 text-sm">
          {items.map((x) => (
            <li key={x.id} className="flex justify-between gap-3">
              <span>
                {x.to ? (
                  <Link to={x.to} className="font-mono text-primary hover:underline">
                    {x.id}
                  </Link>
                ) : (
                  <span className="font-mono">{x.id}</span>
                )}
                <span className="block text-xs text-muted-foreground">{x.label}</span>
              </span>
              {Number.isFinite(x.ttc) && <b className="tabular-nums">{formatEUR(x.ttc)}</b>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-4 py-3 text-sm text-muted-foreground">{t('returnsV2.detail.noDocs')}</p>
      )}
    </Panel>
  );
}

function PortalActions({ c }: { c: Ctx }) {
  const { r, busy, run, viewer } = c;
  const { t } = useTranslation();
  const ErrLine = c.ErrLine;
  if (viewer) return null;
  if (['REQUESTED', 'APPROVED'].includes(r.status) && !r.receivedAt) {
    return (
      <Panel title={t('returnsV2.detail.cancelTitle')}>
        <div className="grid gap-2 px-4 py-3">
          <p className="text-sm text-muted-foreground">{t('returnsV2.detail.cancelHint')}</p>
          <ErrLine k="cancel" />
          <div>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() =>
                void run(
                  'cancel',
                  () => apiClient.post(`/returns/${r.id}/cancel`, {}, cmdHeaders()),
                  t('returnsV2.toast.cancelled')
                )
              }
            >
              {t('returnsV2.detail.cancelBtn')}
            </Button>
          </div>
        </div>
      </Panel>
    );
  }
  return null;
}

function StaffActions({ c }: { c: Ctx }) {
  const { r, busy, run, can, userId, settledAll } = c;
  const { t } = useTranslation();
  const ErrLine = c.ErrLine;
  const s = r.status;
  if (s === 'REQUESTED') {
    if (!can('decide'))
      return (
        <div className="rounded-md bg-primary-soft px-3 py-2 text-sm text-primary">
          {t('returnsV2.detail.waitManager')}
        </div>
      );
    if (r.createdById === userId)
      return (
        <div className="rounded-md bg-warning/[.13] px-3 py-2 text-sm font-medium text-warning">
          {t('returnsV2.detail.selfCreated')}
        </div>
      );
    return <DecidePanel c={c} />;
  }
  if (s === 'APPROVED') {
    const early = !!r.retractShippingProof && !r.retractRefundedOn;
    return (
      <Panel title={t('returnsV2.detail.expectedTitle')}>
        <div className="grid gap-3 px-4 py-3">
          <ErrLine k="recv" />
          <div className="flex flex-wrap gap-2">
            {can('receive') && (
              <Button
                disabled={busy}
                onClick={() =>
                  void run(
                    'recv',
                    () =>
                      apiClient.post('/returns/receive', { number: r.returnNumber }, cmdHeaders()),
                    t('returnsV2.toast.received', { number: r.returnNumber })
                  )
                }
              >
                {t('returnsV2.action.receivePart')}
              </Button>
            )}
            {can('counter') && (
              <Button
                variant="destructive"
                disabled={busy}
                onClick={() =>
                  void run(
                    'recv',
                    () => apiClient.post(`/returns/${r.id}/cancel`, {}, cmdHeaders()),
                    t('returnsV2.toast.cancelled')
                  )
                }
              >
                {t('returnsV2.detail.cancelBtn')}
              </Button>
            )}
            {r.retractRefundDue && !r.retractShippingProof && can('counter') && (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() =>
                  void run(
                    'recv',
                    () => apiClient.post(`/returns/${r.id}/shipping-proof`, {}, cmdHeaders()),
                    t('returnsV2.toast.proof')
                  )
                }
              >
                {t('returnsV2.action.proof')}
              </Button>
            )}
          </div>
          {early && can('counter') && <SettlePanel c={c} early />}
        </div>
      </Panel>
    );
  }
  if (s === 'RECEIVED') {
    if (!can('receive'))
      return (
        <div className="rounded-md bg-primary-soft px-3 py-2 text-sm text-primary">
          {t('returnsV2.detail.waitControl')}
        </div>
      );
    return <ControlPanel c={c} />;
  }
  if (s === 'INSPECTED' && !settledAll) {
    return can('counter') ? (
      <Panel title={t('returnsV2.detail.refundTitle')}>
        <div className="grid gap-3 px-4 py-3">
          <SettlePanel c={c} />
        </div>
      </Panel>
    ) : (
      <div className="rounded-md bg-primary-soft px-3 py-2 text-sm text-primary">
        {t('returnsV2.detail.waitCounter')}
      </div>
    );
  }
  if (s === 'INSPECTED' && settledAll) {
    return (
      <div className="rounded-md bg-primary-soft px-3 py-2 text-sm text-primary">
        {t('returnsV2.detail.refundRunning')}{' '}
        <Link className="underline" to="/refunds">
          {t('returnsV2.detail.refundsLink')}
        </Link>
      </div>
    );
  }
  if (s === 'AWAITING_SUPPLIER') {
    const waiting = r.lines.filter((l) => l.waitSupplier && !l.supplierOutcome && !l.goodwillBy);
    return (
      <Panel title={t('returnsV2.detail.supplierTitle')}>
        <div className="grid gap-3 px-4 py-3">
          <p className="text-sm text-muted-foreground">
            {t('returnsV2.detail.supplierHint', {
              brf: r.documents.supplierReturns
                .map((x) => `${x.number} (${x.supplierName})`)
                .join(', '),
            })}
          </p>
          {can('manager') && waiting.map((l) => <Goodwill c={c} key={l.id} l={l} />)}
          <ErrLine k="gw" />
        </div>
      </Panel>
    );
  }
  return (
    <Panel>
      <p className="px-4 py-3 text-sm text-muted-foreground">
        {t(`returnsV2.detail.final.${s}`, { defaultValue: t('returnsV2.detail.final.none') })}
      </p>
    </Panel>
  );
}

function Goodwill({ c, l }: { c: Ctx; l: ReturnLine }) {
  const { r, busy, run } = c;
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  return (
    <div className="grid items-end gap-2 sm:grid-cols-[1fr_auto]">
      <Field label={t('returnsV2.detail.goodwillLabel', { sku: l.sku })}>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('returnsV2.detail.goodwillHint')}
        />
      </Field>
      <Button
        variant="outline"
        disabled={busy}
        onClick={() =>
          void run(
            'gw',
            () => apiClient.post(`/returns/${r.id}/lines/${l.id}/goodwill`, { note }, cmdHeaders()),
            t('returnsV2.toast.goodwill')
          )
        }
      >
        {t('returnsV2.detail.goodwillBtn')}
      </Button>
    </div>
  );
}

function DecidePanel({ c }: { c: Ctx }) {
  const { r, busy, run } = c;
  const { t } = useTranslation();
  const ErrLine = c.ErrLine;
  const pending = r.lines.filter((l) => l.decision === null);
  const [dec, setDec] = useState<
    Record<string, { accept: boolean | null; note: string; waiveFee: boolean; dep: string }>
  >(
    Object.fromEntries(
      pending.map((l) => [l.id, { accept: null, note: '', waiveFee: false, dep: '' }])
    )
  );
  const set = (id: string, p: Partial<(typeof dec)[string]>) =>
    setDec((d) => ({ ...d, [id]: { ...d[id], ...p } }));
  const save = () =>
    run(
      'decide',
      () =>
        apiClient.post(
          `/returns/${r.id}/decide`,
          {
            decisions: Object.fromEntries(
              pending
                .filter((l) => dec[l.id].accept !== null)
                .map((l) => {
                  const x = dec[l.id];
                  return [
                    l.id,
                    {
                      accept: x.accept,
                      note: x.note,
                      waiveFee: x.waiveFee,
                      depreciationHt: x.dep === '' ? undefined : Number(x.dep.replace(',', '.')),
                    },
                  ];
                })
            ),
          },
          cmdHeaders()
        ),
      t('returnsV2.toast.decided')
    );
  return (
    <Panel title={t('returnsV2.detail.decideTitle')}>
      <div className="grid gap-3 px-4 py-3">
        {pending.map((l) => {
          const x = dec[l.id];
          const dep = l.reason === 'RETRACTATION' && l.condition === 'MOUNTED';
          return (
            <div key={l.id} className="grid gap-2 rounded-md border p-3">
              <div className="text-sm">
                <b className="font-mono">{l.sku}</b> × {l.quantity} · {reasonLabel(t, l.reason)} ·{' '}
                {condLabel(t, l.condition)}
              </div>
              {l.assessmentMsgs.length > 0 && (
                <ul className="list-disc pl-5 text-sm">
                  {l.assessmentMsgs.map((m, i) => (
                    <li key={i}>{msgText(t, m)}</li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2">
                {[true, false].map((a) => (
                  <label
                    key={String(a)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm',
                      x.accept === a && 'border-primary bg-primary-soft'
                    )}
                  >
                    <input
                      type="radio"
                      name={`dec-${l.id}`}
                      checked={x.accept === a}
                      onChange={() => set(l.id, { accept: a })}
                    />
                    {a ? t('returnsV2.detail.accept') : t('returnsV2.detail.refuse')}
                  </label>
                ))}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <Field label={t('returnsV2.detail.decisionNote')}>
                  <Input value={x.note} onChange={(e) => set(l.id, { note: e.target.value })} />
                </Field>
                {dep && (
                  <Field label={t('returnsV2.detail.depreciation')}>
                    <Input
                      inputMode="decimal"
                      value={x.dep}
                      onChange={(e) => set(l.id, { dep: e.target.value })}
                      placeholder="0"
                    />
                  </Field>
                )}
                {l.amounts.feeHt > 0 && (
                  <label className="flex items-center gap-2 self-end text-sm">
                    <input
                      type="checkbox"
                      checked={x.waiveFee}
                      onChange={(e) => set(l.id, { waiveFee: e.target.checked })}
                    />
                    {t('returnsV2.detail.waiveFee', {
                      amount: formatEUR(l.amounts.feeHt * (1 + (l.vat ?? 20) / 100)),
                    })}
                  </label>
                )}
              </div>
            </div>
          );
        })}
        <ErrLine k="decide" />
        <div>
          <Button disabled={busy} onClick={() => void save()}>
            {t('returnsV2.detail.saveDecision')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function ControlPanel({ c }: { c: Ctx }) {
  const { r, busy, run, can } = c;
  const { t } = useTranslation();
  const ErrLine = c.ErrLine;
  const todo = r.lines.filter((l) => l.decision === 'ACCEPTED' && !l.control);
  const [ctl, setCtl] = useState<
    Record<
      string,
      { qty: number; scan: string; seen: string; result: string; note: string; dep: string }
    >
  >(
    Object.fromEntries(
      todo.map((l) => [
        l.id,
        {
          qty: l.quantity,
          scan: '',
          seen: l.condition ?? 'INTACT',
          note: '',
          dep: '',
          result:
            l.kind === 'CORE'
              ? 'CORE_OK'
              : DEFECT.includes(l.reason ?? '')
                ? 'DEFECTIVE'
                : 'RESALEABLE',
        },
      ])
    )
  );
  const set = (id: string, p: Partial<(typeof ctl)[string]>) =>
    setCtl((c) => ({ ...c, [id]: { ...c[id], ...p } }));
  const norm = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const refOk = (l: ReturnLine, scan: string) =>
    scan ? norm(scan) === norm(l.sku) || (!!l.ean && scan.trim() === l.ean) : null;
  const save = () =>
    run(
      'control',
      () =>
        apiClient.post(
          `/returns/${r.id}/control`,
          {
            results: Object.fromEntries(
              todo.map((l) => {
                const x = ctl[l.id];
                const ok = refOk(l, x.scan);
                return [
                  l.id,
                  {
                    receivedQty: x.qty,
                    refOk: ok ?? undefined,
                    seen: l.kind === 'CORE' ? undefined : x.seen,
                    result: x.result,
                    note: x.note,
                    ...(can('manager') && l.reason === 'RETRACTATION' && x.dep !== ''
                      ? { depreciationHt: Number(x.dep.replace(',', '.')) }
                      : {}),
                  },
                ];
              })
            ),
          },
          cmdHeaders()
        ),
      t('returnsV2.toast.controlled')
    );
  return (
    <Panel title={t('returnsV2.detail.controlTitle')}>
      <div className="grid gap-3 px-4 py-3">
        {todo.map((l) => {
          const x = ctl[l.id];
          const ok = refOk(l, x.scan);
          const results =
            l.kind === 'CORE'
              ? ['CORE_OK', 'CORE_KO']
              : ['RESALEABLE', 'DEFECTIVE', 'DAMAGED', 'REFUSED'];
          return (
            <div key={l.id} className="grid gap-2 rounded-md border p-3">
              <div className="text-sm">
                <b className="font-mono">{l.sku}</b> {l.brand} × {l.quantity} ·{' '}
                {reasonLabel(t, l.reason)} ·{' '}
                {t('returnsV2.detail.declared', {
                  cond:
                    l.kind === 'CORE' ? t('returnsV2.detail.usedPart') : condLabel(t, l.condition),
                })}
              </div>
              <div className="grid gap-2 sm:grid-cols-5">
                <Field label={t('returnsV2.detail.received')}>
                  <Select
                    value={String(x.qty)}
                    onChange={(e) => set(l.id, { qty: Number(e.target.value) })}
                  >
                    {Array.from({ length: l.quantity + 1 }, (_, n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('returnsV2.detail.scan')} className="sm:col-span-2">
                  <div className="flex gap-2">
                    <Input
                      className={cn(
                        'font-mono',
                        ok === false && 'border-destructive ring-2 ring-destructive/20'
                      )}
                      value={x.scan}
                      onChange={(e) => set(l.id, { scan: e.target.value })}
                      placeholder={t('returnsV2.detail.scanHint')}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => set(l.id, { scan: l.sku })}
                      title={t('returnsV2.detail.matchesTitle')}
                    >
                      {t('returnsV2.detail.matches')}
                    </Button>
                  </div>
                  <span
                    className={cn(
                      'mt-1 block text-xs normal-case tracking-normal',
                      ok === false
                        ? 'text-destructive'
                        : ok
                          ? 'text-success'
                          : 'text-muted-foreground'
                    )}
                  >
                    {ok === null
                      ? t('returnsV2.detail.mustMatch', { sku: l.sku })
                      : ok
                        ? t('returnsV2.detail.refOk')
                        : t('returnsV2.detail.refKo')}
                  </span>
                </Field>
                {l.kind === 'ITEM' && (
                  <Field label={t('returnsV2.detail.seen')} className="sm:col-span-2">
                    <Select value={x.seen} onChange={(e) => set(l.id, { seen: e.target.value })}>
                      {CONDITIONS.map((k) => (
                        <option key={k} value={k}>
                          {condLabel(t, k)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                <Field label={t('returnsV2.detail.result')} className="sm:col-span-2">
                  <Select value={x.result} onChange={(e) => set(l.id, { result: e.target.value })}>
                    {results.map((k) => (
                      <option key={k} value={k}>
                        {t(`returnsV2.result.${k}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('returnsV2.detail.controlNote')} className="sm:col-span-2">
                  <Input value={x.note} onChange={(e) => set(l.id, { note: e.target.value })} />
                </Field>
                {can('manager') && l.reason === 'RETRACTATION' && (
                  <Field label={t('returnsV2.detail.depreciation')}>
                    <Input
                      inputMode="decimal"
                      value={x.dep}
                      onChange={(e) => set(l.id, { dep: e.target.value })}
                      placeholder={t('returnsV2.detail.emptyNone')}
                    />
                  </Field>
                )}
              </div>
            </div>
          );
        })}
        <p className="text-xs text-muted-foreground">{t('returnsV2.detail.destinationRule')}</p>
        <ErrLine k="control" />
        <div>
          <Button disabled={busy} onClick={() => void save()}>
            {t('returnsV2.detail.validateControl')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function SettlePanel({ c, early }: { c: Ctx; early?: boolean }) {
  const { r, busy, run, err, setErr, can, unsettledReady } = c;
  const { t } = useTranslation();
  const ErrLine = c.ErrLine;
  const lines = early
    ? r.lines.filter((l) => l.decision === 'ACCEPTED' && !l.settled)
    : unsettledReady;
  const legalLine = lines.some((l) => l.reason === 'RETRACTATION' || l.legalNote === 'CONFORMITY');
  const replaceable =
    lines.length > 0 && lines.every((l) => DEFECT.includes(l.reason ?? '') && l.kind === 'ITEM');
  const opts: Array<[string, string, string]> = [];
  if (!r.noReceipt)
    opts.push([
      'ORIGIN',
      t(r.channel === 'WEB' ? 'returnsV2.settle.originWeb' : 'returnsV2.settle.origin'),
      t('returnsV2.settle.originHint'),
    ]);
  opts.push([
    'VOUCHER',
    t('returnsV2.settle.voucher'),
    legalLine
      ? t('returnsV2.settle.voucherLegal')
      : r.noReceipt
        ? t('returnsV2.settle.voucherOnly')
        : t('returnsV2.settle.voucherHint'),
  ]);
  if (replaceable && !early)
    opts.push(['EXCHANGE', t('returnsV2.settle.exchange'), t('returnsV2.settle.exchangeHint')]);
  if (!r.noReceipt && !early)
    opts.push(['TRANSFER', t('returnsV2.settle.transfer'), t('returnsV2.settle.transferHint')]);
  const [mode, setMode] = useState(opts[0][0]);
  const [agreed, setAgreed] = useState(false);
  const amount =
    Math.round(
      (lines.reduce((s, l) => s + l.amounts.ttc, 0) +
        (r.shippingSettled ? 0 : r.shippingRefundTtc)) *
        100
    ) / 100;
  const save = () =>
    run(
      'settle',
      () =>
        apiClient.post(`/returns/${r.id}/settle`, { mode, customerAgreed: agreed }, cmdHeaders()),
      t('returnsV2.toast.settled', { amount: formatEUR(amount) })
    );
  return (
    <div className="grid gap-3">
      {early && <p className="text-sm text-muted-foreground">{t('returnsV2.settle.early')}</p>}
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-muted-foreground">{t('returnsV2.settle.toGive')}</span>
        <b className="text-xl tabular-nums">{formatEUR(amount)}</b>
      </div>
      <div className="grid gap-2">
        {opts.map(([k, label, hint]) => (
          <label
            key={k}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2',
              mode === k && 'border-primary bg-primary-soft'
            )}
          >
            <input
              type="radio"
              name="smode"
              className="mt-1"
              checked={mode === k}
              onChange={() => {
                setMode(k);
                setErr(null);
              }}
            />
            <span>
              <b className="text-sm">{label}</b>
              <span className="block text-xs text-muted-foreground">{hint}</span>
            </span>
          </label>
        ))}
      </div>
      {mode === 'VOUCHER' && legalLine && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          {t('returnsV2.settle.agree')}
        </label>
      )}
      {(r.cashApprovalRequestedBy || r.cashApprovedBy) && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
            r.cashApprovedBy ? 'bg-success/[.12] text-success' : 'bg-warning/[.13] text-warning'
          )}
        >
          {r.cashApprovedBy
            ? t('returnsV2.settle.cashOk', { by: r.cashApprovedBy })
            : t('returnsV2.settle.cashAsked', { by: r.cashApprovalRequestedBy })}
          {!r.cashApprovedBy && can('manager') && (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                void run(
                  'settle',
                  () => apiClient.post(`/returns/${r.id}/cash-approve`, {}, cmdHeaders()),
                  t('returnsV2.toast.cashApproved')
                )
              }
            >
              {t('returnsV2.settle.approveCash')}
            </Button>
          )}
        </div>
      )}
      <ErrLine k="settle" />
      <div className="flex flex-wrap gap-2">
        <Button disabled={busy} onClick={() => void save()}>
          {t('returnsV2.settle.validate')}
        </Button>
        {err?.key === 'settle' &&
          err.code === 'RETURN_CASH_APPROVAL' &&
          !r.cashApprovalRequestedBy && (
            <Button
              variant="outline"
              disabled={busy}
              onClick={() =>
                void run(
                  'settle',
                  () => apiClient.post(`/returns/${r.id}/cash-approval-request`, {}, cmdHeaders()),
                  t('returnsV2.toast.cashAsked')
                )
              }
            >
              {t('returnsV2.settle.askManager')}
            </Button>
          )}
      </div>
    </div>
  );
}

function LineRow({ l, portal }: { l: ReturnLine; portal: boolean }) {
  const { t } = useTranslation();
  const a = l.amounts;
  const vat = 1 + (l.vat ?? 20) / 100;
  return (
    <tr className="border-b last:border-b-0">
      <td className={tdCls}>
        <b className="font-mono text-xs">{l.sku}</b> {l.brand}
        <div className="text-xs text-muted-foreground">{l.name}</div>
      </td>
      <td className={tdCls}>
        {l.orderNumber ? (
          <span className="font-mono text-xs">{l.deliveryNotes[0] ?? l.orderNumber}</span>
        ) : (
          t('returnsV2.detail.noReceipt')
        )}
        {l.unitHt != null && (
          <div className="text-xs text-muted-foreground">
            {t('returnsV2.detail.unit', { amount: formatEUR(l.unitHt * vat) })}
          </div>
        )}
      </td>
      <td className={cn(tdCls, 'text-right tabular-nums')}>{l.quantity}</td>
      <td className={tdCls}>
        {reasonLabel(t, l.reason)}
        <div className="text-xs text-muted-foreground">
          {l.kind === 'ITEM' ? condLabel(t, l.condition) : t('returnsV2.detail.usedPart')}
        </div>
        {l.warranty && (l.warranty.plate || l.warranty.description) && (
          <div className="text-xs text-muted-foreground">
            {[
              l.warranty.plate,
              l.warranty.km ? `${l.warranty.km} km` : null,
              l.warranty.mountedAt
                ? t('returnsV2.detail.mountedOn', { date: formatDate(l.warranty.mountedAt) })
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
            {l.warranty.description && <div>{l.warranty.description}</div>}
          </div>
        )}
      </td>
      <td className={tdCls}>
        {l.decision === 'ACCEPTED' ? (
          <>
            <Badge variant="success">{t('returnsV2.detail.accepted')}</Badge>
            <div className="text-xs text-muted-foreground">
              {l.decidedAuto ? t('returnsV2.detail.byRules') : l.decidedBy}
              {l.decisionNote ? ` : ${l.decisionNote}` : ''}
            </div>
          </>
        ) : l.decision === 'REFUSED' ? (
          <>
            <Badge variant="destructive">{t('returnsV2.detail.refused')}</Badge>
            <div className="text-xs text-muted-foreground">
              {l.decidedBy} : {l.decisionNote}
            </div>
          </>
        ) : l.legacy ? (
          '—'
        ) : (
          <Badge variant="warning">{t('returnsV2.detail.toDecide')}</Badge>
        )}
        {!portal && l.decision === null && l.assessmentMsgs.length > 0 && (
          <div className="mt-1 text-xs text-warning">
            {l.assessmentMsgs.map((m) => msgText(t, m)).join(' ')}
          </div>
        )}
      </td>
      <td className={tdCls}>
        {l.control ? (
          <>
            <b>{t(`returnsV2.result.${l.control.result}`)}</b>
            <div className="text-xs text-muted-foreground">
              {t('returnsV2.detail.receivedN', { n: l.control.receivedQty })}
              {l.control.seen && l.kind === 'ITEM' ? ` · ${condLabel(t, l.control.seen)}` : ''}
              {l.control.note ? ` · ${l.control.note}` : ''}
            </div>
            {!portal && l.destination && (
              <div className="text-xs text-muted-foreground">
                → {t(`returnsV2.dest.${l.destination}`)}
                {l.destinationNote
                  ? ` · ${t(`returnsV2.destNote.${l.destinationNote}`, { defaultValue: l.destinationNote })}`
                  : ''}
              </div>
            )}
            {l.waitSupplier && (
              <div className="text-xs text-muted-foreground">
                {l.goodwillBy
                  ? t('returnsV2.detail.goodwillBy', { by: l.goodwillBy })
                  : l.supplierOutcome
                    ? t(
                        l.supplierOutcome === 'ACCEPTED'
                          ? 'returnsV2.detail.supplierYes'
                          : 'returnsV2.detail.supplierNo',
                        { note: l.supplierNote ?? '' }
                      )
                    : t('returnsV2.detail.supplierWait')}
              </div>
            )}
          </>
        ) : l.legacy ? (
          <span className="text-xs text-muted-foreground">
            {t('returnsV2.detail.legacyControl', {
              accepted: l.legacy.accepted,
              refused: l.legacy.refused,
            })}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className={cn(tdCls, 'text-right tabular-nums')}>
        <b>{formatEUR(a.ttc)}</b>
        {a.feeHt > 0 && (
          <div className="text-xs text-muted-foreground">
            {t('returnsV2.detail.feeLine', { pct: a.feePct, amount: formatEUR(a.feeHt * vat) })}
          </div>
        )}
        {l.feeWaivedBy && (
          <div className="text-xs text-muted-foreground">
            {t('returnsV2.detail.feeWaived', { by: l.feeWaivedBy })}
          </div>
        )}
        {a.depositHt > 0 && (
          <div className="text-xs text-muted-foreground">
            {t('returnsV2.detail.coreLine', { amount: formatEUR(a.depositHt * vat) })}
          </div>
        )}
        {a.depreciationHt > 0 && (
          <div className="text-xs text-muted-foreground">
            {t('returnsV2.detail.depLine', { amount: formatEUR(a.depreciationHt * vat) })}
          </div>
        )}
      </td>
    </tr>
  );
}
